import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Search, Upload, X } from 'lucide-react';
import { uploadsApi, type RecentImage } from '../../../api/endpoints/uploads';
import { useImagePicker } from '../../../contexts/ImagePickerContext';

// Rendered inside the chat panel when the bot has called request_image_choice.
// Shows a grid of recent product images (filterable by description text), an
// upload button, and a cancel. Resolves the picker promise on action.

export function ImagePickerOverlay() {
  const { storeId } = useParams<{ storeId: string }>();
  const picker = useImagePicker();
  const [items, setItems] = useState<RecentImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isOpen = picker.pending !== null;

  useEffect(() => {
    if (!isOpen || !storeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    uploadsApi.recentImages(storeId, 24)
      .then((images) => {
        if (!cancelled) setItems(images);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load images');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, storeId]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((img) => {
      if (img.description?.toLowerCase().includes(q)) return true;
      if (img.descriptionEn?.toLowerCase().includes(q)) return true;
      if (img.productTitle?.toLowerCase().includes(q)) return true;
      if (img.tags?.some((t) => t.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [items, query]);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleFileChosen = async (file: File) => {
    if (!storeId) return;
    if (file.size > 8 * 1024 * 1024) {
      setError('Image too large (max 8 MB)');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
      const uploaded = await uploadsApi.uploadImage(storeId, base64);
      picker.resolveWithUpload(uploaded);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const purposeText = picker.pending?.purpose;
  const hintText = picker.pending?.hint;

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        background: 'var(--color-bg-secondary)',
        display: 'flex',
        flexDirection: 'column',
        direction: 'rtl',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          borderBottom: '1px solid var(--color-border-default)',
          background: 'linear-gradient(155deg, #1E2B42, #0A2248 60%, #010101)',
          color: '#fff',
        }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>اختر صورة</div>
          {(purposeText || hintText) && (
            <div style={{ fontSize: 10.5, color: '#bfbdbd', marginTop: 2 }}>
              {[purposeText, hintText].filter(Boolean).join(' · ')}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={picker.resolveWithCancel}
          aria-label="إلغاء"
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: 'none',
            width: 30,
            height: 30,
            borderRadius: 8,
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <X size={14} />
        </button>
      </div>

      <div style={{ padding: '10px 14px', display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search
            size={12}
            style={{
              position: 'absolute',
              top: '50%',
              insetInlineStart: 10,
              transform: 'translateY(-50%)',
              color: 'var(--color-text-muted)',
            }}
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ابحث في الصور (وصف، وسم، اسم منتج)..."
            style={{
              width: '100%',
              background: 'var(--color-bg-tertiary)',
              border: '1px solid var(--color-border-default)',
              borderRadius: 10,
              padding: '8px 12px 8px 28px',
              fontSize: 12,
              color: 'var(--color-text-primary)',
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
        </div>
        <button
          type="button"
          onClick={handleUploadClick}
          disabled={uploading}
          style={{
            background: '#D2E9FE',
            color: '#0A2248',
            border: 'none',
            borderRadius: 10,
            padding: '8px 12px',
            fontSize: 12,
            fontWeight: 600,
            cursor: uploading ? 'default' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
          {uploading ? 'جارٍ الرفع...' : 'رفع جديدة'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFileChosen(f);
            e.target.value = '';
          }}
        />
      </div>

      {error && (
        <div style={{ padding: '4px 14px', fontSize: 11, color: '#E89292' }}>{error}</div>
      )}

      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '6px 14px 14px',
        }}
      >
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
            جارٍ تحميل الصور...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
            {query.trim() ? 'ما لقينا نتايج مطابقة. جرب كلمة ثانية أو ارفع صورة جديدة.' : 'ما عندك صور بعد. ارفع وحدة عشان تبدأ.'}
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 6,
            }}
          >
            {filtered.map((img) => (
              <button
                key={img.filename}
                type="button"
                onClick={() => picker.resolveWithRecent(img)}
                title={img.description || img.productTitle || img.filename}
                style={{
                  position: 'relative',
                  padding: 0,
                  border: '1px solid var(--color-border-default)',
                  borderRadius: 10,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  background: 'var(--color-bg-tertiary)',
                  aspectRatio: '1 / 1',
                }}
              >
                <img
                  src={`${img.url}?width=250`}
                  alt={img.description || img.filename}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  loading="lazy"
                />
                {img.productTitle && (
                  <div
                    style={{
                      position: 'absolute',
                      insetInlineStart: 0,
                      insetInlineEnd: 0,
                      bottom: 0,
                      padding: '4px 6px',
                      fontSize: 9.5,
                      color: '#fff',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.65), transparent)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      textAlign: 'start',
                    }}
                  >
                    {img.productTitle}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
